import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { getArticleBySlug } from "@/lib/articles.functions";

const articleQuery = (slug: string) =>
  queryOptions({
    queryKey: ["article", slug],
    queryFn: () => getArticleBySlug({ data: { slug } }),
  });

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ context, params }) => {
    const res = await context.queryClient.ensureQueryData(articleQuery(params.slug));
    if (!res.article) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Article — Bacha Way Travel" }] };
    const title = `${a.title} — Bacha Way Travel`;
    const desc = a.excerpt || a.title;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:url", content: `https://bachawaytravel.com/blog/${a.slug}` },
      { property: "og:type", content: "article" },
    ];
    if (a.cover_image_url) {
      meta.push({ property: "og:image", content: a.cover_image_url });
      meta.push({ name: "twitter:image", content: a.cover_image_url });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: `https://bachawaytravel.com/blog/${a.slug}` }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl text-foreground">Article not found</h1>
      <p className="mt-3 text-muted-foreground">This article may have been moved or unpublished.</p>
      <Link to="/blog" className="mt-6 inline-block rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">
        Back to blog
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-2xl text-foreground">Couldn't load this article</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: ArticlePage,
});

function renderContent(text: string) {
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="mt-10 font-display text-2xl md:text-3xl text-foreground">
          {trimmed.slice(3)}
        </h2>
      );
    }
    if (trimmed.startsWith("# ")) {
      return (
        <h2 key={i} className="mt-10 font-display text-3xl md:text-4xl text-foreground">
          {trimmed.slice(2)}
        </h2>
      );
    }
    const imgMatch = trimmed.match(/^!\[(.*?)\]\((.+?)\)$/);
    if (imgMatch) {
      return (
        <figure key={i} className="my-8">
          <img
            src={imgMatch[2]}
            alt={imgMatch[1] || ""}
            loading="lazy"
            className="w-full rounded-2xl border border-border"
          />
          {imgMatch[1] && (
            <figcaption className="mt-2 text-center text-xs text-muted-foreground">{imgMatch[1]}</figcaption>
          )}
        </figure>
      );
    }
    return (
      <p key={i} className="mt-5 text-[1.05rem] leading-[1.85] text-foreground/85 whitespace-pre-wrap">
        {trimmed}
      </p>
    );
  });
}

function ArticlePage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(articleQuery(slug));
  const a = data.article!;
  const date = a.published_at ? new Date(a.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-3xl px-6 py-16 md:py-20">
          {a.category && (
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-white/80">{a.category}</div>
          )}
          <h1 className="mt-4 font-display text-3xl md:text-5xl leading-[1.1]">{a.title}</h1>
          {a.excerpt && <p className="mt-5 text-white/80 text-lg leading-relaxed">{a.excerpt}</p>}
          <div className="mt-6 flex items-center gap-3 text-sm text-white/70">
            {a.author && <span>{a.author}</span>}
            {a.author && date && <span>·</span>}
            {date && <span>{date}</span>}
          </div>
        </div>
      </section>

      {a.cover_image_url && (
        <div className="bg-background">
          <div className="mx-auto max-w-4xl px-6 -mt-8">
            <img
              src={a.cover_image_url}
              alt={a.title}
              className="w-full rounded-2xl border border-border shadow-elegant"
            />
          </div>
        </div>
      )}

      <article className="bg-background">
        <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
          {renderContent(a.content)}
          <div className="mt-16 border-t border-border pt-8">
            <Link to="/blog" className="text-sm font-medium text-primary hover:underline">
              ← Back to all articles
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
